import {
  Component,
  OnInit,
  NgZone
} from '@angular/core';
import {NavigationEnd, Router, RouterOutlet, Event as RouterEventType} from '@angular/router';
import {SidebarComponent} from './sidebar/sidebar.component';
import {HeaderComponent} from './header/header.component';
import {NzContentComponent, NzHeaderComponent, NzLayoutComponent, NzSiderComponent} from 'ng-zorro-antd/layout';
import {CommonModule} from '@angular/common';
import {filter} from 'rxjs';
import {de_DE, en_US, NzI18nService} from 'ng-zorro-antd/i18n';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {AuthService} from './services/auth.service';
import { SessionTelemetryService } from './services/session-telemetry.service';
import {ProgressComponent} from './progress/progress.component';
import {OnboardingProgressWidgetComponent} from './progress/onboarding-progress-widget.component';
import {NzBackTopModule} from 'ng-zorro-antd/back-top';
import {NzAlertModule} from 'ng-zorro-antd/alert';
import {PlatformAnnouncementBannerComponent} from './platform-announcement-banner/platform-announcement-banner.component';
import {PlatformAnnouncementService} from './services/platform-announcement.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    SidebarComponent,
    HeaderComponent,
    NzLayoutComponent,
    NzHeaderComponent,
    NzSiderComponent,
    NzContentComponent,
    CommonModule,
    ProgressComponent,
    OnboardingProgressWidgetComponent,
    PlatformAnnouncementBannerComponent,
    NzBackTopModule,
    TranslateModule,
    NzAlertModule
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.less'
})
export class AppComponent implements OnInit{
  theme: boolean = false;  // Initialize with a default theme value (true for dark, false for light)
  showHeader: boolean = false;
  isLandingPage: boolean = false;
  showProgress = true;
  isAdmin = false;
  isCompanyLandingPage = false;
  isResetPasswordPage = false;
  isMobileScreen = false;
  isLoginPage = false;
  isMobileDesktopPage = false;
  isAuthLinkErrorPage = false;
  isStandaloneRouteView = false;
  isAcceptInvitePage = false;
  isProgressVisible = false;
  sidebarCollapsed = false;

  constructor(
    private router: Router,
    private nzI18nService: NzI18nService,
    private translate: TranslateService,
    private authService: AuthService,
    private platformAnnouncementService: PlatformAnnouncementService,
    private sessionTelemetryService: SessionTelemetryService,
    private ngZone: NgZone) {}

  checkMobile(): void {
    this.isMobileScreen = window.innerWidth <= 768;
  }

  onProgressUpdate(_reached100: boolean) {
    // ProgressComponent now controls its own visibility internally
  }

  onProgressVisibilityChange(visible: boolean): void {
    this.isProgressVisible = visible;
  }

  onSidebarCollapsedChange(collapsed: boolean): void {
    this.sidebarCollapsed = collapsed;
  }

  private scrollToRoutedContentIfProgressVisible(): void {
    // Only when progress component is rendered
    if (!(this.showHeader && !this.isAdmin && this.isProgressVisible)) return;

    this.ngZone.runOutsideAngular(() => {
      // Let the new route render first
      setTimeout(() => {
        const mainContent = document.querySelector('.main-content') as HTMLElement | null;

        // Scroll the layout container if it’s scrollable
        if (mainContent && mainContent.scrollHeight > mainContent.clientHeight) {
          mainContent.scrollTo({ top: mainContent.scrollHeight, behavior: 'smooth' });
        }

        // Also scroll window as fallback (some pages scroll on body)
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
      }, 0);
    });
  }

  // Update theme when it changes
  onThemeChange(newTheme: boolean): void {
    this.theme = newTheme;
    localStorage.setItem('theme', JSON.stringify(newTheme));
    this.updateTheme();
  }

  ngOnInit(): void {
    this.sessionTelemetryService.initialize();
    const savedLanguage = localStorage.getItem('selectedLanguage') || 'de';
    const initialLanguage = savedLanguage === 'en' ? 'en' : 'de';
    this.nzI18nService.setLocale(initialLanguage === 'en' ? en_US : de_DE);
    this.translate.setDefaultLang(initialLanguage);
    this.translate.use(initialLanguage);
    this.router.events.pipe(
      filter((event: RouterEventType) => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.isStandaloneRouteView = this.isStandaloneRoute(event.urlAfterRedirects || event.url);
      this.sessionTelemetryService.handleRouteChange(event.urlAfterRedirects || event.url);
      this.isLoginPage = event.url.startsWith('/login') || event.url.startsWith('/register');
      this.isLandingPage = event.url.startsWith('/landing-page');
      this.isResetPasswordPage = event.url.startsWith('/reset-password');
      this.isMobileDesktopPage = event.url.startsWith('/mobile-desktop');
      this.isAuthLinkErrorPage = event.url.startsWith('/auth-link-error');
      this.isAcceptInvitePage = event.url.startsWith('/accept-invite');
      if (!this.authService.isAuthenticated()) {
        this.showHeader = false;
        this.platformAnnouncementService.reset();
        if (this.isLandingPage) {
          // Treat as an external user
          const urlSegments = event.url.split('/');
          const companyId = urlSegments[2];
          const propertyId = urlSegments[3];

          if (companyId && propertyId) {
            // Navigate to the specified landing page if IDs are valid
            this.router.navigate(['landing-page', companyId, propertyId]);
          } else {
            // Redirect to a default landing page if IDs are missing or invalid
            this.router.navigate(['/landing-page', 'defaultCompanyId', 'defaultPropertyId']);
          }
        } else if (this.isStandaloneRouteView) {
          return;
        } else if (!event.url.startsWith('/login') && !event.url.startsWith('/register')) {
          this.isCompanyLandingPage = false; // explicitly set false
          this.router.navigate(['/login']);
        } else {
          this.isCompanyLandingPage = false;
        }
      } else {
        if (event.url.startsWith('/company-landing-page')) {
          this.isCompanyLandingPage = true;
        }
        // Handle authenticated users
        this.checkUserRole();
        // Define the routes where the header should be hidden
        const hiddenHeaderRoutes = ['/login', '/register', '/landing-page', '/auth-callback', '/reset-password', '/company-landing-page', '/mobile-desktop', '/auth-link-error', '/accept-invite'];
        this.showHeader = !hiddenHeaderRoutes.some(route => event.url.startsWith(route));
        this.syncPlatformAnnouncement();
        this.scrollToRoutedContentIfProgressVisible();
      }
      // Initialize theme based on localStorage value
      const storedTheme = localStorage.getItem('theme');
      this.theme = storedTheme ? JSON.parse(storedTheme) : false;
      this.updateTheme();
    });
    this.checkMobile();
    window.addEventListener('resize', () => this.checkMobile());
  }

  checkUserRole(): void {
    const token = this.authService.getToken();
    if (token) {
      const decodedToken = this.authService.decodeToken(token);
      const userRole = decodedToken?.role || '';
      this.isAdmin = userRole === 'Admin';
    }
  }

  private syncPlatformAnnouncement(): void {
    if (this.showHeader && this.authService.isAuthenticated() && !this.authService.isCurrentUserAdmin()) {
      this.platformAnnouncementService.loadActiveOnce().subscribe();
      return;
    }
    this.platformAnnouncementService.reset();
  }

  updateTheme(): void {
    if (!this.isCompanyLandingPage){
      // Add or remove classes based on the theme
      document.body.classList.toggle('dark-theme', this.theme);
      document.body.classList.toggle('light-theme', !this.theme);
    }
  }

  private isStandaloneRoute(url: string): boolean {
    const standalonePrefixes = ['/login', '/register', '/reset-password', '/auth-callback', '/accept-invite', '/auth-link-error', '/landing-page', '/company-landing-page'];
    return standalonePrefixes.some((prefix) => url.startsWith(prefix));
  }
}
